import type {BootstrapConfig} from '../types'
import _ from 'lodash'
import {
    configs,
    type ConfigName,
    extendWithExperimentalFeatures,
    extendWithFeatures
} from '@wix/document-manager-configs'

import {ReportableError} from '@wix/document-manager-utils'
import type {Experiment, DSConfig} from '@wix/document-services-types'
const NO_ORIGIN = 'none'

const defaultOptions: DSConfig = {
    origin: NO_ORIGIN,
    modulesList: [],
    extensionsList: [],
    supportedFragments: {},
    disableFragments: false,
    isReadOnly: false,
    noUndo: false,
    disableSave: false,
    disableAutoSave: false,
    disableCSaveValidationOnInitialization: false,
    supportsPlatformInitialization: true,
    closeSectionGaps: false,
    runStylesGC: false,
    disableMobileConversion: false,
    selectiveCompsDS: false,
    supportMobile: true,
    loadsMiniSiteManager: false,
    initialSeed: false,
    disableCommonConfig: false,
    schemaDevMode: false,
    autosaveRestore: 'true',
    dontCollectFixerVersionData: false,
    keepMultiLingualModelsFromServer: false,
    lazyLoadPages: false,
    enableRefComponentFallbackToViewerManager: true,
    transactionActionsLimit: 10000,
    csaveSnapshotActionsLimit: 10000,
    csaveTransactionsLimit: 100,
    minNumOfPagesToCalculateRenderHints: 100,
    loadingPartialPages: false
}

const optionalParams = [
    'signatureSeed',
    'isDebugMode',
    'undoRedoConfig',
    'untilTransactionId',
    'isReadOnly',
    'enableScopes',
    'enableRepeatersInScopes',
    'enableRefComponentFallbackToViewerManager',
    'doNotSyncRepeatedLayout',
    'allowOnlyPublishRC',
    'experimentalConfigOptions',
    'enableWidgetInRepeater',
    'shouldUseMobileLibrary',
    'enforceStrictValidationEverywhere',
    'enforceNoWhitelistDalStrictSchemaValidation',
    'strictBlacklistBaselineForEmployees',
    'runPreSaveOperationInQueue',
    'cachePublishedSite'
]

const getDsConfig = (
    bootstrapConfig: BootstrapConfig,
    experimentInstance: Experiment,
    overrides: Partial<DSConfig> = {}
): DSConfig => {
    if (!bootstrapConfig.configName || !configs[bootstrapConfig.configName]) {
        throw new ReportableError({
            message: `The value for configName (${
                bootstrapConfig.configName ?? ''
            }) does not map to a valid configuration`,
            errorType: 'incorrectConfigurationParamError'
        })
    }

    const configName = bootstrapConfig.configName as ConfigName

    const configOptions = _(bootstrapConfig)
        .pick([...optionalParams, ..._.keys(defaultOptions)])
        .assign({
            origin: bootstrapConfig.dsOrigin
        })
        .defaults(configs[configName!], defaultOptions)
        .assign(overrides)
        .value()

    if (!configOptions.origin || configOptions.origin === NO_ORIGIN) {
        throw new Error(
            'You must define dsOrigin parameter in order to use documentServices - please speak to html-server team for a key'
        )
    }

    if (typeof localStorage !== 'undefined' && localStorage.getItem('dm-debug')) {
        configOptions.isDebugMode = true
    }

    return {
        configName,
        ...extendWithExperimentalFeatures(
            extendWithFeatures(configOptions, bootstrapConfig.features),
            experimentInstance
        )
    }
}

export {getDsConfig}
